import { STEP } from "#src/constants/step.ts";

import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { PRODUCT_TYPE } from "#src/constants/products.ts";

import { useStepBuildStore } from "#src/stores/step-build.ts";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.ts";
import { useStepQuoteStore } from "#src/stores/step-quote.ts";
import { useStepBenefitEstimationStore } from "#src/stores/step-benefit-estimation.ts";
import { useStepPreQuoteInformationStore } from "#src/stores/step-pre-quote-information.ts";
import { useStepLifePriorityStore } from "#src/stores/step-life-priority.ts";
import { useStepCompareStore } from "#src/stores/step-compare.ts";
import { useBirthdateForm } from "#src/components/steps/birthdate/birthdate-form.composable.js";
import {
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { arrayOnlyContains } from "#src/util/helpers.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed, ref } from "vue";
import { type InsuredType } from "#src/types/Insured.ts";
import {
  type BaseStepStoreState,
  type ApiFlowStep,
} from "#src/types/StepStore.ts";
import { type Product } from "#src/types/products/Product.ts";

interface BirthdateState extends BaseStepStoreState {}

interface BirthdateStep extends ApiFlowStep, ToSetupState<BirthdateState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): BirthdateState {
  return {
    inactive: false,
  };
}

function initializeBirthdateStep(pinia: Pinia): BirthdateStep {
  const state = generateInitialState<BirthdateState>(initialState());

  function $reset() {
    resetState<BirthdateState>(state, initialState());
  }

  const step = computed(() => STEP.BIRTHDATE);
  const previous = computed(() => {
    const build = useStepBuildStore(pinia);
    if (build.inactive) return build.previous;
    return build.step;
  });
  const next = computed(() => {
    const instanceSettingsStore = useInstanceSettingsStore(pinia);

    const quote = useStepQuoteStore(pinia);
    const types = instanceSettingsStore.available.map((v: Product) => {
      if ("subtype" in v) return v.subtype || v.type;
      return v.type;
    });
    if (
      arrayOnlyContains(types, [PRODUCT_TYPE.ACCIDENTAL_DEATH]) ||
      arrayOnlyContains(types, [PRODUCT_TYPE.FINAL_EXPENSE]) ||
      arrayOnlyContains(types, [
        PRODUCT_TYPE.ACCIDENTAL_DEATH,
        PRODUCT_TYPE.FINAL_EXPENSE,
      ])
    ) {
      return quote.step;
    }

    const benefitEstimation = useStepBenefitEstimationStore(pinia);
    if (
      arrayOnlyContains(types, [PRODUCT_TYPE.WHOLE_LIFE]) ||
      arrayOnlyContains(types, [PRODUCT_TYPE.ACCUMULATION_IUL])
    ) {
      return benefitEstimation.step;
    }

    const preQuoteInformation = useStepPreQuoteInformationStore(pinia);
    if (arrayOnlyContains(types, [PRODUCT_TYPE.INCOME_RIDER])) {
      return preQuoteInformation.step;
    }

    const lifePriority = useStepLifePriorityStore(pinia);
    if (lifePriority.availablePriorities.length > 1) {
      return lifePriority.step;
    }

    if (instanceSettingsStore.onlyOnePermanentProduct) return quote.step;

    const compare = useStepCompareStore(pinia);
    return compare.step;
  });
  function generateForm(module: InsuredType, args: any) {
    return useBirthdateForm(pinia, module, args);
  }
  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.INSURED_BIRTHDATE];
  }

  return {
    ...state,
    $reset,
    next,
    step,
    previous,
    generateForm,
    setStepRequirements,
  };
}

export const useStepBirthdateStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, BirthdateStep>("step-birthdate", () => {
    return initializeBirthdateStep(pinia);
  })(pinia, hot);
};
